import uniqBy from 'lodash/uniqBy';
import { combineEpics, ofType } from 'redux-observable';
import { of } from 'rxjs';
import { map, mergeMap, switchMap, tap } from 'rxjs/operators';

import { organizationTypeIdSel } from '@context/duck/selectors';
import { esbApi } from '@modules/rtk/esb-api';
import tagFactory from '@modules/rtk/utils/tagFactory';
import { getIniativeJson } from '@modules/shared/utils/initiativesUtils';
import { setSelectedRosterKeys } from '@shared/duck/actions';
import { organizationGuidSel } from '@shared/duck/selectors/general.selectors';
import { intl } from '@shared/localization/translate';
import toastService from '@toasts/service';
import { userIdSel } from '@user/duck/selectors';
import { catchAndReport } from '@utils/rxjs/operators';

import {
  loadAndOpenActivity,
  personsAndIdsActivitySel,
  personsToRemoveSel,
  savingAction,
  selectedActivitySel,
  setTime,
} from '../../common';
import {
  LOAD_AND_OPEN_SERVICE_MODAL,
  RECORD_SERVICE_REQUEST,
  RECORD_SERVICE_RESPONSE,
  closeService,
  loadAndOpenServiceModalError,
  openEditService,
  recordServiceError,
  recordServiceResponse,
} from './actions';
import { activeTabSel, personsSel } from './selectors';
import services from './services';

const recordServiceEpic$ = (action$, state$) =>
  action$.pipe(
    ofType(RECORD_SERVICE_REQUEST),
    switchMap(({ payload }) => {
      const state = state$.value;
      const { activityData } = payload;
      const persons = personsSel(state);
      const personsToRemove = personsToRemoveSel(state);
      const loggedInUserId = userIdSel(state);
      const organizationGuid = organizationGuidSel(state);
      const organizationTypeId = organizationTypeIdSel(state);
      const selectedActivity = selectedActivitySel(state);
      const isAdvancedMode = activeTabSel(state) == 'advanced';
      const { personsInActivity } = personsAndIdsActivitySel(state);
      let validActivityData = { ...activityData };
      if (activityData.allDay) {
        validActivityData.startTime = setTime();
        validActivityData.endTime = setTime(23, 45);
      }

      validActivityData = {
        ...validActivityData,
        benefitGroup: getIniativeJson(activityData),
      };

      const uniquePersons = uniqBy(
        [...personsInActivity, ...persons],
        'userId',
      );

      const personsUserIds = uniquePersons?.map(person => person.userId) || [];
      const personsGuids =
        uniquePersons?.map(person => person.personGuid) || [];

      const tagsToInvalidate = personsUserIds.map(userId =>
        tagFactory.UserActivitySummary.userSummary(userId),
      );

      // Invalidate org activities
      tagsToInvalidate.push(
        tagFactory.Activities.orgActivities(organizationGuid),
      );

      // Invalidate youth activities
      tagsToInvalidate.push(
        tagFactory.Activities.youthActivities(selectedActivity?.personGuid),
        ...personsUserIds.map(userId =>
          tagFactory.Activities.youthActivities(userId),
        ),
        ...personsGuids.map(personGuid =>
          tagFactory.Activities.youthActivities(personGuid),
        ),
      );

      return services
        .saveServiceActivity$({
          ...selectedActivity,
          ...validActivityData,
          personsToRemove,
          loggedInUserId,
          organizationGuid,
          organizationTypeId: +organizationTypeId,
          persons: uniquePersons,
          isAdvancedMode,
        })
        .pipe(
          mergeMap(() => {
            const { closeOnSuccess } = payload;
            const actions = [
              recordServiceResponse(),
              // Invalidate tags
              esbApi.util.invalidateTags(tagsToInvalidate),
            ].concat(
              closeOnSuccess ? [closeService(), setSelectedRosterKeys([])] : [],
            );

            return of(...actions);
          }),
          tap(({ type }) => {
            if (type === RECORD_SERVICE_RESPONSE) {
              toastService.success(
                intl.formatMessage(
                  {
                    id: `progress.common.${
                      persons.length ? 'saveSuccessParticipants' : 'saveSuccess'
                    }`,
                  },
                  {
                    name: activityData.name,
                    type: selectedActivity.id
                      ? savingAction.UPDATED
                      : savingAction.CREATED,
                    count: persons.length,
                    participants: persons
                      .map(({ personShortFullName }) => personShortFullName)
                      .join(', '),
                  },
                ),
              );
            }
          }),
          catchAndReport(err => of(recordServiceError(err))),
        );
    }),
  );

const loadLongCruiseByIdEpic$ = action$ =>
  action$.pipe(
    ofType(LOAD_AND_OPEN_SERVICE_MODAL),
    map(({ payload }) =>
      loadAndOpenActivity({
        ...payload,
        successActionFn: openEditService,
        errorActionFn: loadAndOpenServiceModalError,
      }),
    ),
  );

export default combineEpics(recordServiceEpic$, loadLongCruiseByIdEpic$);
